import { useState } from 'react';
import PropTypes from 'prop-types';
import { useAnalytics } from 'hooks/useAnalytics';
import { InView } from 'react-intersection-observer';
import { Transition } from '@headlessui/react';
import { ButtonSmall, InputMedium } from 'elements/Forms';
import Image from 'components/Image';

const InPostResourceCta = ({ name, image, heading, text, button }) => {
  const { identify, track } = useAnalytics();
  const [submitted, setSubmitted] = useState(false);

  const submit = async (event) => {
    event.preventDefault();

    let email = event.target.email.value;
    email = email.trim().toLowerCase();

    setSubmitted(true);

    await identify(email);
    await track(
      'Resource CTA Submitted',
      {
        email,
        name,
        heading,
        text,
        button,
        lastName: 'Null',
      },
      {
        integrations: {
          Salesforce: true,
        },
      },
    );
  };

  return (
    <InView
      as="span"
      onChange={(inView) => {
        if (inView) {
          track('Resource CTA Viewed', {
            name,
            heading,
            text,
            button,
          });
        }
      }}
      triggerOnce
      className="not-prose"
    >
      <span className="flex w-full flex-row gap-10 rounded-2xl bg-blue-02 p-4 shadow-md sm:mt-20 sm:px-10 sm:pt-10 sm:pb-0">
        <span className="hidden leading-none sm:relative sm:-top-20 sm:-mb-20 sm:block sm:w-2/5">
          {image && (
            <Image src={image} width="900" height="1256" alt={heading} />
          )}
        </span>
        <span className="w-3/5 flex-grow text-left">
          <span className="block text-2xl font-bold tracking-tight text-grey-10 sm:text-3xl">
            {heading}
          </span>
          <span className="block text-base text-grey-06 sm:text-lg ">
            {text}
          </span>

          <span
            className="relative mt-4 block sm:pb-10"
            css={{ minHeight: '36px' }}
          >
            <Transition
              as="span"
              show={!submitted}
              leave="transition-opacity duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <form onSubmit={submit} className="flex flex-col gap-y-4">
                <label htmlFor="emailAddress" className="w-full">
                  <span className="sr-only">Email</span>
                  <InputMedium
                    type="email"
                    autoComplete="email"
                    name="email"
                    id="email"
                    placeholder="Enter your email"
                    required
                  />
                </label>
                <ButtonSmall className="w-full">{button}</ButtonSmall>
              </form>
            </Transition>

            <Transition
              as="span"
              show={submitted}
              enter="absolute left-0 right-0 transition-opacity delay-200 duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
            >
              <span className="block rounded-md bg-blue-06 p-2 text-center text-sm text-white">
                <span className="font-bold">Thank you.</span> Check your inbox
                for the download.
              </span>
            </Transition>
          </span>
        </span>
      </span>
    </InView>
  );
};

InPostResourceCta.propTypes = {
  name: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  button: PropTypes.string.isRequired,
};

export default InPostResourceCta;
